<template>
  <div class="season-page">
    <!-- Header -->
    <header class="sp-header">
      <button class="sp-header__btn" @click="$router.go(-1)">
        <van-icon name="arrow-left" size="20" color="#fff" />
      </button>
      <h1 class="sp-header__title">{{ $lang.season_pass_title || 'Season Pass' }}</h1>
      <button class="sp-header__btn sp-header__btn--right" @click="$jumpTo('/seasonHistory')">
        <van-icon name="clock-o" size="20" color="#ff5a1f" />
      </button>
    </header>

    <main class="sp-main">
      <div v-if="loading" class="sp-loading">
        <van-loading color="#ff5a1f" size="32" />
      </div>

      <template v-else>
      <div v-if="readFailureMessage" class="sp-read-warning">
        {{ readFailureMessage }}
      </div>
      <!-- Progress Section -->
      <section class="sp-progress">
        <div class="sp-progress__row">
          <div>
            <p class="sp-progress__label">{{ $lang.season_current_progress || 'Current Progress' }}</p>
            <p class="sp-progress__value">{{ currentPoints }} <span class="sp-progress__unit">PTS</span></p>
          </div>
          <p class="sp-progress__next">{{ $lang.season_next_reward || 'Next Reward' }}: {{ nextReward }} PTS</p>
        </div>
        <div class="sp-progress__bar">
          <div class="sp-progress__fill" :style="{ width: progressPercent + '%' }">
            <div class="sp-progress__fill-shine"></div>
          </div>
        </div>
      </section>

      <!-- Upgrade Pass Card -->
      <section class="sp-upgrade">
        <div class="sp-upgrade__card">
          <div class="sp-upgrade__head">
            <div>
              <h3 class="sp-upgrade__title">{{ $lang.season_upgrade_pass || 'UPGRADE PASS' }}</h3>
              <p class="sp-upgrade__subtitle">{{ $lang.season_unlock_premium || 'Unlock Premium Tiers' }}</p>
            </div>
            <!-- Treasure Chest Icon -->
            <div class="sp-chest-icon">
              <div class="sp-chest-icon__body">
                <div class="sp-chest-icon__lid"></div>
                <div class="sp-chest-icon__band sp-chest-icon__band--left"></div>
                <div class="sp-chest-icon__band sp-chest-icon__band--right"></div>
                <div class="sp-chest-icon__lock"></div>
              </div>
            </div>
          </div>

          <div class="sp-upgrade__tiers">
            <!-- Junior -->
            <div class="sp-tier">
              <div class="sp-tier__header">
                <div class="sp-shield-sm"></div>
                <span class="sp-tier__name sp-tier__name--silver">{{ $lang.season_junior || 'Junior' }}</span>
              </div>
              <p class="sp-tier__desc">{{ $lang.season_junior_desc || 'Silver rewards & standard XP boosts' }}</p>
            </div>
            <!-- Senior -->
            <div class="sp-tier">
              <div class="sp-tier__header">
                <div class="sp-chest-sm">
                  <div class="sp-chest-sm__lid"></div>
                  <div class="sp-chest-sm__lock"></div>
                </div>
                <span class="sp-tier__name sp-tier__name--gold">{{ $lang.season_senior || 'Senior' }}</span>
              </div>
              <p class="sp-tier__desc sp-tier__desc--bright">{{ $lang.season_senior_desc || 'Gold items & max XP multipliers' }}</p>
            </div>
          </div>

          <div class="sp-upgrade__actions">
            <button class="sp-btn sp-btn--silver" :disabled="!upgradePaymentAvailable" @click="handleUpgrade('junior')">{{ $lang.season_unlock_junior || 'Unlock Junior' }}</button>
            <button class="sp-btn sp-btn--primary" :disabled="!upgradePaymentAvailable" @click="handleUpgrade('senior')">{{ $lang.season_go_senior || 'Go Senior Pro' }}</button>
          </div>

          <!-- Decorative blurs -->
          <div class="sp-upgrade__blur sp-upgrade__blur--top"></div>
          <div class="sp-upgrade__blur sp-upgrade__blur--bottom"></div>
        </div>
      </section>

      <!-- Reward Track -->
      <section class="sp-track">
        <h2 class="sp-track__title">
          <span class="sp-track__line"></span>
          {{ $lang.season_reward_track || 'Reward Track' }}
        </h2>

        <!-- Column Headers -->
        <div class="sp-track__header">
          <div class="sp-track__col sp-track__col--pts">{{ $lang.season_points || 'Points' }}</div>
          <div class="sp-track__col">{{ $lang.season_basic || 'Basic' }}</div>
          <div class="sp-track__col">{{ $lang.season_junior || 'Junior' }}</div>
          <div class="sp-track__col">{{ $lang.season_senior || 'Senior' }}</div>
        </div>

        <!-- Milestone Rows -->
        <div class="sp-track__rows">
          <div
            v-for="(ms, idx) in milestones"
            :key="idx"
            class="sp-milestone"
          >
            <!-- Points Column -->
            <div class="sp-milestone__pts" :class="{ 'sp-milestone__pts--active': currentPoints >= ms.points - 100 }">
              <span class="sp-milestone__pts-val">{{ ms.points }}</span>
              <span class="sp-milestone__pts-label">Points</span>
            </div>

            <!-- Basic Column -->
            <div class="sp-milestone__reward" :class="{ 'sp-milestone__reward--dim': ms.basic.status === 'locked' }">
              <div class="sp-coin-icon">
                <span class="sp-coin-icon__letter">G</span>
              </div>
              <span class="sp-milestone__name">{{ ms.basic.name }}</span>
              <button
                class="sp-claim-btn"
                :class="'sp-claim-btn--' + ms.basic.status"
                :disabled="ms.basic.status !== 'claimable'"
                @click="handleClaim('basic', ms)"
              >{{ getStatusLabel(ms.basic.status) }}</button>
            </div>

            <!-- Junior Column -->
            <div class="sp-milestone__reward" :class="{ 'sp-milestone__reward--dim': ms.junior.status === 'locked' }">
              <div class="sp-shield-icon"></div>
              <span class="sp-milestone__name">{{ ms.junior.name }}</span>
              <button
                class="sp-claim-btn"
                :class="'sp-claim-btn--' + ms.junior.status"
                :disabled="ms.junior.status !== 'claimable'"
                @click="handleClaim('junior', ms)"
              >{{ getStatusLabel(ms.junior.status) }}</button>
              <div v-if="ms.junior.status === 'locked'" class="sp-lock-badge">
                <van-icon name="lock" size="10" color="#fff" />
              </div>
            </div>

            <!-- Senior Column -->
            <div class="sp-milestone__reward" :class="{ 'sp-milestone__reward--dim': ms.senior.status === 'locked' }">
              <div class="sp-chest-icon-sm">
                <div class="sp-chest-icon-sm__body">
                  <div class="sp-chest-icon-sm__lid"></div>
                  <div class="sp-chest-icon-sm__lock"></div>
                </div>
              </div>
              <span class="sp-milestone__name">{{ ms.senior.name }}</span>
              <button
                class="sp-claim-btn"
                :class="'sp-claim-btn--' + ms.senior.status + (ms.senior.status === 'claimed' ? ' sp-claim-btn--gold' : '')"
                :disabled="ms.senior.status !== 'claimable'"
                @click="handleClaim('senior', ms)"
              >{{ getStatusLabel(ms.senior.status) }}</button>
              <div v-if="ms.senior.status === 'locked'" class="sp-lock-badge">
                <van-icon name="lock" size="10" color="#fff" />
              </div>
            </div>
          </div>
          <div v-if="milestones.length === 0" class="sp-empty">
            <van-icon name="records" size="48" color="var(--vk-t3)" />
            <p>{{ $lang.season_history_empty || 'No season rewards yet' }}</p>
          </div>
        </div>
      </section>
      </template>
    </main>

    <!-- Win Ticker Footer -->
    <div v-if="marqueeMessages.length" class="sp-ticker">
      <div class="sp-ticker__track">
        <div
          v-for="(msg, idx) in marqueeMessages"
          :key="idx"
          class="sp-ticker__item"
          :class="{ 'sp-ticker__item--alt': idx % 2 !== 0 }"
        >
          <van-icon :name="idx % 2 === 0 ? 'star' : 'fire'" size="16" color="#ff5a1f" />
          <span v-safe-html.rich="msg"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { TaskCenterWeeklyInit, ClaimProgressReward } from '@/api/common'
import { failMessage as rewardFailMessage, hasExplicitRewardIssueProof as hasRewardIssueProof, hasUnavailableMarker, isClaimSuccess as rewardClaimSuccess, isNeedLogin, isOkResponse, responseContent } from './rewardApi'

export default {
  name: 'SeasonPass',
  data() {
    return {
      loading: true,
      currentPoints: 0,
      nextReward: 0,
      milestones: [],
      marqueeMessages: [],
      upgradePayUrl: '',
      upgradePayShow: false,
      upgradePaymentResponse: null,
      upgradePaymentAvailable: false,
      seasonRewardWritable: true,
      readFailureMessage: ''
    }
  },
  computed: {
    trackTarget() {
      if (this.milestones.length) return this.milestones[this.milestones.length - 1].points
      return this.nextReward || this.currentPoints || 0
    },
    progressPercent() {
      if (!this.trackTarget) return 0
      return Math.min(100, Math.round((this.currentPoints / this.trackTarget) * 100))
    }
  },
  mounted() {
    this.fetchSeason()
  },
  methods: {
    safeNumber(value, fallback = 0) {
      const n = Number(typeof value === 'string' ? value.replace(/,/g, '') : value)
      return Number.isFinite(n) ? n : fallback
    },
    safeArray(value) {
      return Array.isArray(value) ? value : []
    },
    firstValue(source, keys, fallback = '') {
      if (!source) return fallback
      for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key]
      }
      return fallback
    },
    requireLogin() {
      if (this.$bus) this.$bus.$emit('openLogin')
    },
    failMessage(res, fallback) {
      return rewardFailMessage(res, fallback || this.$lang.mc_claim_failed || 'This feature is temporarily unavailable. Please try again later.')
    },
    hasVisibleSeasonData() {
      return this.milestones.length > 0 || this.marqueeMessages.length > 0 || this.currentPoints > 0 || this.nextReward > 0
    },
    preserveReadFailureMessage(message) {
      const base = message || this.$lang.reward_no_data || 'No rewards found'
      if (!this.hasVisibleSeasonData() || String(base).includes('已保留已加载赛季数据')) return base
      return `${base}，已保留已加载赛季数据。`
    },
    setReadFailure(res, fallback) {
      this.readFailureMessage = this.preserveReadFailureMessage(this.failMessage(res, fallback || this.$lang.reward_no_data || 'No rewards found'))
      this.$toast({ message: this.readFailureMessage, icon: 'cross' })
    },
    failCloseSeasonRead() {
      this.seasonRewardWritable = false
      this.upgradePaymentAvailable = false
      this.upgradePaymentResponse = null
      this.upgradePayShow = false
      this.upgradePayUrl = ''
      this.milestones = this.milestones.map(milestone => {
        const next = { ...milestone }
        ;['basic', 'junior', 'senior'].forEach(tier => {
          const reward = next[tier]
          if (reward && reward.status === 'claimable') {
            next[tier] = {
              ...reward,
              status: 'locked'
            }
          }
        })
        return next
      })
    },
    hasWriteGuardMarker(source, depth = 0) {
      if (!source || typeof source !== 'object' || depth > 5) return false
      if (hasUnavailableMarker(source)) return true
      const blockingKeys = ['readOnly', 'readOnlyMode', 'readonly', 'read_only', 'read_only_mode', 'mock', 'mockMode', 'mock_mode', 'mockFallback', 'mock_fallback', 'stub', 'unimplemented', 'notImplemented', 'not_implemented', 'notSupported', 'not_supported', 'unsupported', 'failed', 'failClosed', 'fail_closed', 'writeBlocked', 'write_blocked', 'technicalMarker', 'technical_marker', 'technicalException', 'technical_exception', 'backendBlocked', 'backend_blocked', 'backendFailure', 'backend_failure', 'rewardBackendBlocked', 'reward_backend_blocked', 'fallback', 'fallbackUsed', 'fallback_used', 'dataUnavailable', 'data_unavailable', 'falseProof', 'false_proof', 'paymentStatus', 'payment_status', 'paymentStatusUnconfirmed', 'payment_status_unconfirmed', 'unconfirmed']
      const blockingTokens = ['mock', 'mock_mode', 'mock mode', 'mock-mode', 'mock_fallback', 'read_only', 'read_only_mode', 'read only mode', 'read-only-mode', 'readonly', 'readonlymode', 'read only', 'read-only', 'stub', 'unimplemented', 'not_implemented', 'not_supported', 'not supported', 'unsupported', 'failed', 'failclosed', 'fail_closed', 'fail closed', 'fail-closed', 'write_blocked', 'writeblocked', 'write blocked', 'write-blocked', 'writeproof:false', 'write_proof:false', 'write proof:false', 'write-proof:false', 'writeproof false', 'write proof false', 'write-proof-false', 'backend_blocked', 'backendblocked', 'backend blocked', 'backend-blocked', 'backendfailure', 'backend_failure', 'backend failure', 'backend-failure', 'reward_backend_blocked', 'rewardbackendblocked', 'reward backend blocked', 'reward-backend-blocked', 'fallback', 'fallback_used', 'fallback used', 'fallback-used', 'data_unavailable', 'data unavailable', 'data-unavailable', 'dataunavailable', 'falseproof', 'false_proof', 'false proof', 'false-proof', 'technicalmarker', 'technical_marker', 'technical marker', 'technical-marker', 'technicalexception', 'technical_exception', 'technical exception', 'technical-exception', '技术异常', 'paymentstatusunconfirmed', 'payment_status_unconfirmed', 'payment status unconfirmed', 'payment-status-unconfirmed', 'unconfirmed']
      const isActive = value => value === true || value === 1 || blockingTokens.some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      const isFalseProof = value => value === false || value === 0 || ['0', 'false', 'no', 'off'].includes(String(value == null ? '' : value).trim().toLowerCase()) || blockingTokens.some(token => String(value == null ? '' : value).trim().toLowerCase().includes(token))
      if (['writeProof', 'write_proof', 'rewardProof', 'reward_proof'].some(key => isFalseProof(source[key]))) return true
      if (blockingKeys.some(key => isActive(source[key]))) return true
      if ([source.status, source.state, source.result, source.code, source.errorCode, source.msg, source.message, source.errorMessage, source.errorMsg, source.reason, source.desc, source.description, source.detail, source.readOnlyMessage, source.unavailableReason].some(isActive)) return true
      return ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'error', 'errors', 'detail', 'details'].some(key => this.hasWriteGuardMarker(source[key], depth + 1))
    },
    isClaimSuccess(res) {
      return !this.hasWriteGuardMarker(res) && rewardClaimSuccess(res) && this.hasRewardIssueProof(res)
    },
    hasRewardIssueProof(res) {
      return hasRewardIssueProof(res)
    },
    isFalseValue(value) {
      if (value === false || value === 0) return true
      const normalized = String(value == null ? '' : value).trim().toLowerCase()
      return ['false', '0', 'no', 'disabled', 'read_only', 'read_only_mode', 'read-only', 'read-only-mode', 'readonlymode', 'write_blocked', 'write-blocked'].includes(normalized)
    },
    isRewardWritableSource(source) {
      if (!source || typeof source !== 'object') return true
      return !this.hasWriteGuardMarker(source) &&
        !this.isFalseValue(source.rewardWritable) &&
        !this.isFalseValue(source.claimWritable) &&
        !this.isFalseValue(source.writable) &&
        !this.isFalseValue(source.writeEnabled)
    },
    isTrueValue(value) {
      if (value === true || value === 1) return true
      const normalized = String(value == null ? '' : value).trim().toLowerCase()
      return ['true', '1', 'yes', 'claimable', 'available'].includes(normalized)
    },
    paymentToken(value) {
      return String(value == null ? '' : value).trim().toLowerCase()
    },
    isUnavailablePaymentToken(value) {
      const token = this.paymentToken(value)
      return !!token && (
        [
          'mock',
          'mock_fallback',
          'mock-fallback',
          'mock fallback',
          'fallback',
          'fallback_used',
          'fallback used',
          'fallback-used',
          'data_unavailable',
          'dataunavailable',
          'data unavailable',
          'data-unavailable',
          'backend_blocked',
          'backendblocked',
          'backend blocked',
          'backend-blocked',
          'backendfailure',
          'backend_failure',
          'backend failure',
          'backend-failure',
          'read_only',
          'read_only_mode',
          'read-only-mode',
          'read only mode',
          'readonlymode',
          'readonly',
          'read only',
          'read-only',
          'unimplemented',
          'not_implemented',
          'not implemented',
          'not_supported',
          'not-supported',
          'not supported',
          'unsupported',
          'disabled',
          'unavailable',
          'write_blocked',
          'writeblocked',
          'write blocked',
          'write-blocked',
          'writeproof:false',
          'write_proof:false',
          'write proof false',
          'write-proof-false',
          'falseproof',
          'false_proof',
          'false proof',
          'false-proof',
          'technicalexception',
          'technical_exception',
          'technical exception',
          'technical-exception',
          'technicalmarker',
          'technical_marker',
          'technical marker',
          'technical-marker',
          'about:blank',
          'javascript:void(0)'
        ].includes(token) ||
        token.includes('mock') ||
        token.includes('fallback') ||
        token.includes('backend_blocked') ||
        token.includes('backendblocked') ||
        token.includes('backend blocked') ||
        token.includes('backend-blocked') ||
        token.includes('backendfailure') ||
        token.includes('backend_failure') ||
        token.includes('backend failure') ||
        token.includes('backend-failure') ||
        token.includes('data_unavailable') ||
        token.includes('data unavailable') ||
        token.includes('data-unavailable') ||
        token.includes('dataunavailable') ||
        token.includes('read_only') ||
        token.includes('read only') ||
        token.includes('read-only') ||
        token.includes('unimplemented') ||
        token.includes('not_implemented') ||
        token.includes('not implemented') ||
        token.includes('not_supported') ||
        token.includes('not-supported') ||
        token.includes('not supported') ||
        token.includes('write_blocked') ||
        token.includes('writeblocked') ||
        token.includes('write blocked') ||
        token.includes('write-blocked') ||
        token.includes('writeproof:false') ||
        token.includes('write_proof:false') ||
        token.includes('write proof false') ||
        token.includes('write-proof-false') ||
        token.includes('falseproof') ||
        token.includes('false_proof') ||
        token.includes('false proof') ||
        token.includes('false-proof') ||
        token.includes('technicalexception') ||
        token.includes('technical_exception') ||
        token.includes('technical exception') ||
        token.includes('technical-exception') ||
        token.includes('technicalmarker') ||
        token.includes('technical_marker') ||
        token.includes('technical marker') ||
        token.includes('technical-marker') ||
        token.includes('unsupported')
      )
    },
    isSafePaymentProof(value) {
      const text = String(value || '').trim()
      if (!text || text.charAt(0) === '#') return false
      const token = this.paymentToken(text)
      return !this.isUnavailablePaymentToken(token) && ![
        '0',
        'none',
        'null',
        'undefined',
        'placeholder'
      ].includes(token)
    },
    safePaymentUrl(value) {
      const text = String(value || '').trim()
      if (!this.isSafePaymentProof(text)) return ''
      if (/^[a-z][a-z0-9+.-]*:/i.test(text) && !/^https?:\/\//i.test(text)) return ''
      return text
    },
    upgradePaymentUrl(content) {
      if (!content || typeof content !== 'object') return ''
      const url = content.url ||
        content.payUrl ||
        content.paymentUrl ||
        content.cashierUrl ||
        content.checkoutUrl ||
        content.redirectUrl ||
        content.qrcode ||
        content.qrCode ||
        ''
      return this.safePaymentUrl(url)
    },
    hasUpgradePaymentFailure(content) {
      if (!content || typeof content !== 'object') return false
      const status = this.paymentToken(content.status || content.state || content.result)
      return this.hasNonPositiveAffectedRows(content) || [
        content.paySuccess,
        content.success
      ].some(this.isFalseValue) ||
        ['fail', 'failed', 'failure', 'error', 'rejected'].includes(status) ||
        hasUnavailableMarker(content)
    },
    isNegativeProofValue(value) {
      if (value === undefined || value === null || value === '') return false
      if (value === false || value === 0) return true
      const token = this.paymentToken(value)
      if (!token) return false
      return ['0', 'false', 'no', 'off', 'none', 'null', 'undefined', 'placeholder'].includes(token) ||
        this.isUnavailablePaymentToken(token)
    },
    isPositiveProofValue(value) {
      if (value === true || value === 1) return true
      if (typeof value === 'number') return Number.isFinite(value) && value > 0
      const text = String(value == null ? '' : value).trim()
      if (!text) return false
      return !this.isNegativeProofValue(text) && this.isSafePaymentProof(text)
    },
    hasNonPositiveAffectedRows(source, depth = 0) {
      if (!source || typeof source !== 'object' || depth > 5) return false
      const failed = [
        source.affectedRows,
        source.affected_rows
      ].some(value => {
        if (value === undefined || value === null || value === '') return false
        if (value === false || value === 0) return true
        if (value === true || value === 1) return false
        const number = Number(value)
        if (Number.isFinite(number)) return number <= 0
        const token = this.paymentToken(value)
        return ['false', 'no', 'off', 'fail', 'failed', 'none', 'null', 'undefined', 'placeholder'].includes(token) ||
          this.isUnavailablePaymentToken(value)
      })
      if (failed) return true
      return ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'detail', 'details'].some(key => this.hasNonPositiveAffectedRows(source[key], depth + 1))
    },
    hasUpgradePaymentProofMarker(source, depth = 0) {
      if (!source || typeof source !== 'object' || depth > 5) return false
      if (this.hasNonPositiveAffectedRows(source)) return false
      const proofValues = [
        source.paymentUrlProof,
        source.payment_url_proof,
        source.payProof,
        source.pay_proof,
        source.orderProof,
        source.order_proof,
        source.statusProof,
        source.status_proof,
        source.serverOrderStatusProof,
        source.server_order_status_proof
      ]
      const genericProofValues = [
        source.paymentProof,
        source.payment_proof
      ]
      if ([...proofValues, ...genericProofValues].some(value => this.isNegativeProofValue(value))) return false
      if (proofValues.some(value => this.isPositiveProofValue(value))) return true
      return ['content', 'payload', 'extra', 'page', 'data', 'body', 'meta', 'trace', 'diagnostics', 'diagnostic', 'detail', 'details'].some(key => this.hasUpgradePaymentProofMarker(source[key], depth + 1))
    },
    hasUpgradePaymentProof(res, content) {
      return this.hasUpgradePaymentProofMarker(content) || this.hasUpgradePaymentProofMarker(res)
    },
    isUpgradePaymentReady(res) {
      const content = responseContent(res)
      if (this.hasWriteGuardMarker(res) || this.hasNonPositiveAffectedRows(res) || !isOkResponse(res) || !content || typeof content !== 'object' || !Object.keys(content).length) return false
      return !this.hasUpgradePaymentFailure(content) && !!this.upgradePaymentUrl(content) && this.hasUpgradePaymentProof(res, content)
    },
    upgradePaymentCandidateFrom(source) {
      if (!source || typeof source !== 'object') return null
      const directUrl = this.upgradePaymentUrl(source)
      if (directUrl || this.hasUpgradePaymentProofMarker(source) || this.hasWriteGuardMarker(source)) {
        return { status: 'ok', content: source }
      }
      const keys = [
        'upgradePayment',
        'upgrade_payment',
        'seasonUpgradePayment',
        'season_upgrade_payment',
        'payment',
        'pay',
        'checkout',
        'cashier'
      ]
      for (const key of keys) {
        const value = source[key]
        if (value && typeof value === 'object') {
          const nested = this.upgradePaymentCandidateFrom(value)
          if (nested) return nested
        }
      }
      return null
    },
    pickUpgradePaymentResponse(...sources) {
      for (const source of sources) {
        const candidate = this.upgradePaymentCandidateFrom(source)
        if (candidate) return candidate
      }
      return null
    },
    upgradePaymentUnavailableMessage() {
      return this.$lang.season_upgrade_unavailable || 'This feature is temporarily unavailable. Please try again later.'
    },
    isRewardUnavailable(reward) {
      if (!reward) return true
      if (this.seasonRewardWritable === false) return true
      return this.hasWriteGuardMarker(reward) ||
        this.isFalseValue(reward.rewardWritable) ||
        this.isFalseValue(reward.available) ||
        this.isTrueValue(reward.locked)
    },
    isTierClaimSupported(reward, tier) {
      if (tier === 'basic') return true
      return this.isTrueValue(reward && reward.claimSupported)
    },
    extractSeasonContent(content) {
      if (!content) return {}
      const section = content.season ||
        content.seasonPass ||
        content.weekly ||
        content.records ||
        content.data ||
        content
      if (section && section !== content && typeof section === 'object' && !Array.isArray(section)) {
        return {
          ...content,
          ...section
        }
      }
      return section
    },
    firstArray(source, keys) {
      if (!source || typeof source !== 'object') return []
      for (const key of keys) {
        const value = source[key]
        if (Array.isArray(value)) return value
        if (value && typeof value === 'object') {
          const nested = this.firstArray(value, keys)
          if (nested.length) return nested
        }
      }
      return []
    },
    rewardName(reward, tier) {
      const value = this.firstValue(reward, ['name', 'title', 'rewardName', 'desc', 'description'], '')
      if (value) return String(value)
      const amount = this.safeNumber(this.firstValue(reward, ['rewardAmount', 'rewardGold', 'reward', 'prize', 'value', 'amount', 'bonus', 'points'], 0))
      if (amount > 0) return `${this.$formatNumberWithCommas(amount)} ${this.getCurrency || 'Gold'}`
      const fallback = {
        basic: this.$lang.season_basic || 'Basic',
        junior: this.$lang.season_junior || 'Junior',
        senior: this.$lang.season_senior || 'Senior'
      }
      return fallback[tier] || ''
    },
    rewardStatus(reward, points) {
      const tier = arguments.length > 2 ? arguments[2] : 'basic'
      if (!reward) return 'locked'
      const raw = String(this.firstValue(reward, ['status', 'state', 'rewardStatus'], '')).toLowerCase()
      if (reward.claimed === true || reward.isClaimed === true || raw === 'claimed' || raw === 'completed' || raw === 'received') return 'claimed'
      if (this.isRewardUnavailable(reward) || !this.isTierClaimSupported(reward, tier)) return 'locked'
      if (this.isTrueValue(reward.canClaim) || this.isTrueValue(reward.canClaimReward) || raw === 'claimable' || raw === 'available') return 'claimable'
      if (tier === 'basic' && this.currentPoints >= points && reward.locked !== true && reward.available !== false && reward.rewardWritable !== false && reward.readOnly !== true) return 'claimable'
      return 'locked'
    },
    normalizeTierReward(source, tier, points) {
      const reward = source || {}
      return {
        id: this.firstValue(reward, ['id', 'rewardId', 'code'], tier),
        name: this.rewardName(reward, tier),
        status: this.rewardStatus(reward, points, tier)
      }
    },
    normalizeMilestone(item, index) {
      const points = this.safeNumber(this.firstValue(item, ['points', 'threshold', 'point', 'targetPoints', 'thresholdPoints', 'requiredPoints', 'activityPoints', 'minPoints'], 0))
      if (points <= 0) return null
      return {
        id: this.firstValue(item, ['id', 'rewardId', 'code'], points),
        points,
        basic: this.normalizeTierReward(item.basic || item.free || item.base || item.reward || item.rewardInfo || item, 'basic', points),
        junior: this.normalizeTierReward(item.junior || item.silver || item.premium || {}, 'junior', points),
        senior: this.normalizeTierReward(item.senior || item.gold || item.pro || {}, 'senior', points),
        raw: item,
        index
      }
    },
    normalizeMarquee(content) {
      const messages = this.safeArray(
        content.marqueeMessages ||
        content.marquee ||
        content.rewardMessages ||
        content.winnerList
      )
      this.marqueeMessages = messages.map(item => {
        if (typeof item === 'string') return item
        const name = this.firstValue(item, ['name', 'userName', 'nickname', 'account'], '')
        const amount = this.safeNumber(this.firstValue(item, ['amount', 'rewardAmount', 'bonus'], 0))
        if (!name || amount <= 0) return ''
        return `${name} +${this.$formatNumberWithCommas(amount)} ${this.getCurrency || ''}`
      }).filter(Boolean)
    },
    applySeason(content) {
      const section = this.extractSeasonContent(content)
      this.seasonRewardWritable = this.isRewardWritableSource(content) && this.isRewardWritableSource(section)
      this.upgradePaymentResponse = this.pickUpgradePaymentResponse(content, section)
      this.upgradePaymentAvailable = this.isUpgradePaymentReady(this.upgradePaymentResponse)
      const taskRows = this.firstArray(section, ['taskList', 'tasks', 'records', 'list', 'dataList'])
      const claimedPoints = taskRows.reduce((sum, item) => {
        return sum + this.safeNumber(this.firstValue(item, ['points', 'claim_amount', 'claimAmount', 'rewardAmount', 'amount'], 0))
      }, 0)
      this.currentPoints = this.safeNumber(
        this.firstValue(section, ['currentPoints', 'currentPoint', 'points', 'point', 'activityPoints', 'activityPoint', 'totalPoints'], claimedPoints),
        claimedPoints
      )
      const rows = this.firstArray(section, ['milestones', 'progressRewards', 'rewardTrack', 'rewardList', 'rewards', 'records', 'list', 'dataList'])
      const normalized = rows
        .map((item, index) => this.normalizeMilestone(item, index))
        .filter(Boolean)
        .sort((a, b) => a.points - b.points)

      this.milestones = normalized
      const next = normalized.find(item => item.points > this.currentPoints)
      this.nextReward = next ? next.points : (normalized.length ? normalized[normalized.length - 1].points : 0)
      this.normalizeMarquee(section)
    },
    hasReadableSeasonPayload(section) {
      if (!section || typeof section !== 'object') return false
      return this.firstArray(section, ['milestones', 'progressRewards', 'rewardTrack', 'rewardList', 'rewards', 'records', 'list', 'dataList']).length > 0 ||
        this.firstArray(section, ['taskList', 'tasks']).length > 0 ||
        this.safeArray(section.marqueeMessages || section.marquee || section.rewardMessages || section.winnerList).length > 0
    },
    shouldPreserveUnavailableSeasonRead(res, content, section) {
      return this.hasVisibleSeasonData() &&
        !this.hasReadableSeasonPayload(section) &&
        (this.hasWriteGuardMarker(res) || this.hasWriteGuardMarker(content) || this.hasWriteGuardMarker(section))
    },
    async fetchSeason() {
      this.loading = true
      try {
        const res = await TaskCenterWeeklyInit({})
        if (isNeedLogin(res)) {
          this.failCloseSeasonRead()
          this.requireLogin()
        } else if (isOkResponse(res)) {
          const content = responseContent(res)
          const section = this.extractSeasonContent(content)
          if (this.shouldPreserveUnavailableSeasonRead(res, content, section)) {
            this.failCloseSeasonRead()
            this.setReadFailure(res, this.$lang.reward_no_data || 'No rewards found')
            return
          }
          this.readFailureMessage = ''
          this.applySeason(content)
        } else {
          this.failCloseSeasonRead()
          this.setReadFailure(res, this.$lang.reward_no_data || 'No rewards found')
        }
      } catch (e) {
        this.failCloseSeasonRead()
        this.setReadFailure(e, '活动请求失败，请稍后重试。')
      } finally {
        this.loading = false
      }
    },
    handleUpgrade() {
      const upgradeRes = this.upgradePaymentResponse
      if (!this.isUpgradePaymentReady(upgradeRes)) {
        this.upgradePayUrl = ''
        this.upgradePayShow = false
        this.$toast({
          message: this.upgradePaymentUnavailableMessage(),
          icon: 'cross'
        })
        return
      }
      const payUrl = this.upgradePaymentUrl(responseContent(upgradeRes))
      this.upgradePayUrl = payUrl
      this.upgradePayShow = true
    },
    getStatusLabel(status) {
      if (status === 'claimed') return this.$lang.season_claimed || 'CLAIMED'
      if (status === 'claimable') return this.$lang.season_claim || 'Claim'
      return this.$lang.season_locked || 'LOCKED'
    },
    async handleClaim(tier, milestone) {
      const reward = milestone && milestone[tier]
      if (!reward || reward.status !== 'claimable') return
      if (!this.seasonRewardWritable) {
        this.$toast(this.failMessage(null, this.$lang.mc_claim_failed || 'This feature is temporarily unavailable. Please try again later.'))
        return
      }
      if (!this.token) {
        this.requireLogin()
        return
      }
      try {
        const res = await ClaimProgressReward({
          category: 'weekly',
          tier,
          threshold: milestone.points,
          rewardId: reward.id || milestone.id
        })
        if (isNeedLogin(res)) {
          this.requireLogin()
        } else if (this.isClaimSuccess(res)) {
          reward.status = 'claimed'
          const content = responseContent(res)
          const amount = (content.amount || content.rewardAmount || content.reward) || 0
          this.$toast({
            message: amount ? `+${this.$formatNumberWithCommas(amount)} ${this.getCurrency || ''}` : (this.$lang.season_claimed || 'CLAIMED'),
            icon: 'success'
          })
        } else {
          this.$toast(this.failMessage(res, this.$lang.mc_claim_failed || 'This feature is temporarily unavailable. Please try again later.'))
        }
      } catch (e) {
        const networkFallback = this.$lang.network_error === 'Network error'
          ? '活动网络请求失败，请稍后重试。'
          : (this.$lang.network_error || '活动网络请求失败，请稍后重试。')
        this.$toast(this.failMessage(e, networkFallback))
      }
    }
  }
}
</script>

<style lang="less" scoped>
@primary: @brand;   /* 绿→火焰橙(强调/激活) */
@gold: #ffb020;     /* 黄→金(金额/奖) */
@silver: #C0C0C0;   /* 银(段位,中性保留) */
@bg-dark: @vk-bg;   /* 页面底 */
@surface: rgba(255, 255, 255, 0.05);

.season-page {
  min-height: 100vh;
  background: @bg-dark;
  color: #e2e8f0;
}

// ====== HEADER ======
.sp-header {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: fade(@bg-dark, 80%);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &__btn {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    border-radius: 50%;

    &:active {
      background: rgba(255, 255, 255, 0.05);
    }
  }

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 800;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #f1f5f9;
  }
}

.sp-main {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 80px;
}

.sp-read-warning {
  margin: 12px 16px 0;
  padding: 10px 12px;
  border: 1px solid fade(@gold, 30%);
  border-radius: 8px;
  background: fade(@gold, 8%);
  color: @gold;
  font-size: var(--vk-fs-xs);
  line-height: 1.4;
}

.sp-loading,
.sp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 180px;
  color: var(--vk-t3);
  font-size: var(--vk-fs-base);
}

.sp-empty {
  grid-column: 1 / -1;
  padding: 32px 0;
}

// ====== PROGRESS ======
.sp-progress {
  padding: 16px;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 12px;
  }

  &__label {
    font-size: var(--vk-fs-2xs);
    text-transform: uppercase;
    letter-spacing: -0.02em;
    color: var(--vk-t2);
    font-weight: 700;
  }

  &__value {
    font-size: var(--vk-fs-2xl);
    font-weight: 900;
    color: @primary;
    font-style: italic;
    line-height: 1.1;
  }

  &__unit {
    font-size: var(--vk-fs-xs);
    font-weight: 400;
    color: #cbd5e1;
    font-style: normal;
  }

  &__next {
    font-size: var(--vk-fs-2xs);
    color: var(--vk-t2);
  }

  &__bar {
    height: 16px;
    width: 100%;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 999px;
    overflow: hidden;
    padding: 2px;
  }

  &__fill {
    height: 100%;
    background: @primary;
    border-radius: 999px;
    box-shadow: 0 0 10px fade(@primary, 50%);
    position: relative;
    transition: width 0.5s;
  }

  &__fill-shine {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3));
    border-radius: 999px;
  }
}

// ====== UPGRADE PASS CARD ======
.sp-upgrade {
  padding: 0 16px 8px;

  &__card {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    border: 1px solid fade(@primary, 30%);
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), fade(@primary, 5%));
    padding: 20px;
    box-shadow: 0 0 15px fade(@gold, 30%);
  }

  &__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
    z-index: 10;
  }

  &__title {
    color: @primary;
    font-size: var(--vk-fs-lg);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: -0.02em;
    font-style: italic;
  }

  &__subtitle {
    color: var(--vk-t2);
    font-size: var(--vk-fs-2xs);
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.1em;
    margin-top: 4px;
  }

  &__tiers {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-top: 16px;
    position: relative;
    z-index: 10;
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 16px;
    position: relative;
    z-index: 10;
  }

  &__blur {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);

    &--top {
      top: -40px;
      right: -40px;
      width: 128px;
      height: 128px;
      background: fade(@primary, 10%);
    }

    &--bottom {
      bottom: -40px;
      left: -40px;
      width: 96px;
      height: 96px;
      background: fade(@gold, 5%);
    }
  }
}

// Tier info blocks
.sp-tier {
  &__header {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__name {
    font-size: var(--vk-fs-xs);
    font-weight: 900;
    text-transform: uppercase;

    &--silver {
      color: @silver;
    }

    &--gold {
      color: @gold;
    }
  }

  &__desc {
    color: var(--vk-t2);
    font-size: var(--vk-fs-2xs);
    line-height: 1.3;
    margin-top: 4px;

    &--bright {
      color: #f1f5f9;
      font-weight: 500;
    }
  }
}

// Buttons
.sp-btn {
  padding: 10px 0;
  border-radius: 12px;
  font-size: var(--vk-fs-2xs);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  cursor: pointer;
  border: none;
  transition: all 0.2s;

  &--silver {
    flex: 1;
    background: fade(@silver, 10%);
    border: 1px solid fade(@silver, 30%);
    color: @silver;

    &:active {
      background: fade(@silver, 20%);
    }
  }

  &--primary {
    flex: 1.5;
    background: @primary;
    color: @bg-dark;
    box-shadow: 0 0 15px fade(@primary, 40%);

    &:active {
      opacity: 0.9;
    }
  }
}

// ====== REWARD TRACK ======
.sp-track {
  margin-top: 32px;
  padding: 0 16px;

  &__title {
    color: #f1f5f9;
    font-size: var(--vk-fs-sm);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__line {
    display: inline-block;
    width: 32px;
    height: 1px;
    background: @primary;
  }

  &__header {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 8px;
    padding: 0 8px;
    margin-bottom: 16px;
  }

  &__col {
    font-size: var(--vk-fs-2xs);
    font-weight: 700;
    color: var(--vk-t3);
    text-transform: uppercase;
    text-align: center;

    &--pts {
      text-align: left;
    }
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

// ====== MILESTONE ROW ======
.sp-milestone {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 8px;
  align-items: center;
  background: @surface;
  border-radius: 12px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);

  &__pts {
    display: flex;
    flex-direction: column;

    &--active &-val {
      color: @primary;
    }
  }

  &__pts-val {
    font-size: var(--vk-fs-base);
    font-weight: 700;
    font-style: italic;
    color: #cbd5e1;
  }

  &__pts-label {
    font-size: 8px;
    color: var(--vk-t3);
    text-transform: uppercase;
  }

  &__reward {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    position: relative;

    &--dim {
      opacity: 0.6;
    }
  }

  &__name {
    font-size: 7px;
    font-weight: 700;
    color: #cbd5e1;
    text-transform: uppercase;
    text-align: center;
    white-space: nowrap;
  }
}

// ====== CLAIM BUTTONS ======
.sp-claim-btn {
  font-size: 8px;
  font-weight: 900;
  padding: 4px 12px;
  border-radius: 2px;
  text-transform: uppercase;
  width: 100%;
  border: none;
  cursor: pointer;

  &--claimed {
    background: @primary;
    color: @bg-dark;
    box-shadow: 0 0 20px fade(@primary, 80%);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  &--gold {
    background: @gold !important;
    box-shadow: 0 0 20px fade(@gold, 80%) !important;
  }

  &--claimable {
    background: @primary;
    color: @bg-dark;
    box-shadow: 0 0 10px fade(@primary, 30%);
  }

  &--locked {
    background: rgba(255, 255, 255, 0.1);
    color: var(--vk-t2);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
}

// ====== LOCK BADGE ======
.sp-lock-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #0f172a;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  z-index: 20;
}

// ====== CSS ICONS ======

// Gold Coin
.sp-coin-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FFF720, #FFD700, #B8860B);
  position: relative;
  box-shadow: inset -2px -2px 4px rgba(0, 0, 0, 0.6), 2px 4px 8px rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.8) 0%, transparent 50%);
    opacity: 0.7;
  }

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.4);
  }

  &__letter {
    font-size: var(--vk-fs-md);
    font-weight: 900;
    color: #5c4a00;
    position: relative;
    z-index: 1;
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.5);
  }
}

// Shield (small, for tier header)
.sp-shield-sm {
  width: 18px;
  height: 18px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #FFF720, #FFD700, #B8860B);
    border-radius: 2px;
    transform: rotate(45deg);
    box-shadow: inset -1px -1px 2px rgba(0, 0, 0, 0.5), 1px 2px 4px rgba(0, 0, 0, 0.4);
  }
}

// Shield icon (for milestone)
.sp-shield-icon {
  width: 36px;
  height: 36px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, #FFF720, #FFD700, #B8860B);
    border-radius: 4px;
    transform: rotate(45deg);
    box-shadow: inset -2px -2px 4px rgba(0, 0, 0, 0.5), 2px 4px 8px rgba(0, 0, 0, 0.5);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 35% 35%, rgba(255, 255, 255, 1) 0%, transparent 60%);
    border-radius: 4px;
    transform: rotate(45deg);
    opacity: 0.7;
  }
}

// Small chest for tier header
.sp-chest-sm {
  width: 16px;
  height: 12px;
  background: linear-gradient(to bottom, #4d3a2b, #2a1b12);
  border-radius: 1px;
  position: relative;
  box-shadow: 1px 2px 4px rgba(0, 0, 0, 0.6);
  border-bottom: 1px solid rgba(0, 0, 0, 0.6);

  &__lid {
    position: absolute;
    top: -4px;
    left: 0;
    width: 100%;
    height: 6px;
    background: linear-gradient(to top, #5c4033, #8b5e3c);
    border-radius: 3px 3px 0 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.6);
  }

  &__lock {
    position: absolute;
    top: 2px;
    left: 50%;
    transform: translateX(-50%);
    width: 4px;
    height: 6px;
    background: linear-gradient(135deg, #ffb020, #B8860B);
    border-radius: 1px;
  }
}

// Small chest for milestone
.sp-chest-icon-sm {
  margin-bottom: 2px;

  &__body {
    width: 32px;
    height: 24px;
    background: linear-gradient(to bottom, #4d3a2b, #2a1b12);
    border-radius: 2px;
    position: relative;
    box-shadow: 2px 6px 10px rgba(0, 0, 0, 0.6), 0 0 15px fade(@gold, 50%);
    border-bottom: 2px solid rgba(0, 0, 0, 0.6);
  }

  &__lid {
    position: absolute;
    top: -10px;
    left: 0;
    width: 100%;
    height: 14px;
    background: linear-gradient(to top, #5c4033, #8b5e3c);
    border-radius: 8px 8px 0 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.6);

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 8px;
      width: 16px;
      height: 2px;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 9px;
      filter: blur(0.5px);
    }
  }

  &__lock {
    position: absolute;
    top: 2px;
    left: 50%;
    transform: translateX(-50%);
    width: 10px;
    height: 12px;
    background: linear-gradient(135deg, #FFF720, #B8860B);
    border-radius: 2px;
    border: 1px solid #8b6914;
    box-shadow: 0 0 5px rgba(255, 215, 0, 0.4);
  }
}

// Large chest for upgrade card
.sp-chest-icon {
  width: 48px;
  height: 48px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 0 15px rgba(255, 215, 0, 0.4));

  &__body {
    width: 40px;
    height: 32px;
    background: linear-gradient(to bottom, #4d3a2b, #2a1b12);
    border-radius: 2px;
    position: relative;
    box-shadow: 2px 6px 10px rgba(0, 0, 0, 0.6), 0 0 15px fade(@gold, 50%);
    border-bottom: 2px solid rgba(0, 0, 0, 0.6);
  }

  &__lid {
    position: absolute;
    top: -12px;
    left: 0;
    width: 100%;
    height: 16px;
    background: linear-gradient(to top, #5c4033, #8b5e3c);
    border-radius: 8px 8px 0 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.6);

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 10px;
      width: 20px;
      height: 2px;
      background: rgba(255, 255, 255, 0.3);
      border-radius: 9px;
      filter: blur(0.5px);
    }
  }

  &__band {
    position: absolute;
    top: -12px;
    width: 8px;
    height: 44px;
    background: linear-gradient(to right, #cbd5e1, #fff, var(--vk-t2));
    border-left: 1px solid rgba(100, 116, 139, 0.5);
    border-right: 1px solid rgba(100, 116, 139, 0.5);
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.2);

    &--left {
      left: 8px;
    }

    &--right {
      right: 8px;
    }
  }

  &__lock {
    position: absolute;
    top: 2px;
    left: 50%;
    transform: translateX(-50%);
    width: 12px;
    height: 16px;
    background: linear-gradient(135deg, #ffb020, #B8860B);
    border-radius: 2px;
    border: 1px solid #8b6914;
    box-shadow: 0 0 5px rgba(255, 215, 0, 0.4);

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 6px;
      height: 4px;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 1px;
      filter: blur(0.3px);
    }
  }
}

// ====== WIN TICKER FOOTER ======
.sp-ticker {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: fade(@bg-dark, 90%);
  backdrop-filter: blur(20px);
  border-top: 2px solid fade(@primary, 50%);
  padding: 12px 0;
  overflow: hidden;
  box-shadow: 0 -10px 30px fade(@primary, 20%);
  z-index: 40;

  &__track {
    display: flex;
    gap: 48px;
    white-space: nowrap;
    animation: marquee-fast 15s linear infinite;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--vk-fs-xs);
    text-transform: uppercase;
    font-weight: 900;
    font-style: italic;
    color: @primary;

    &--alt {
      color: #f1f5f9;
    }
  }
}

@keyframes marquee-fast {
  0% { transform: translateX(100%); }
  100% { transform: translateX(-150%); }
}

// ====== RESPONSIVE ======
@media (min-width: 769px) {
  .season-page {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }
}
</style>
